<template>
  <div>
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>积分商品管理</span>
        <div class="header-actions">
          <el-button :loading="loading" @click="loadData">刷新</el-button>
          <el-button type="primary" @click="goCreate">新增积分商品</el-button>
        </div>
      </div>

      <el-form :inline="true" :model="query" class="query-form">
        <el-form-item>
          <el-input v-model.trim="query.name" placeholder="商品名称" clearable />
        </el-form-item>
        <el-form-item>
          <el-select v-model="query.status" placeholder="状态" clearable>
            <el-option label="上架" :value="1" />
            <el-option label="下架" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetQuery">重置</el-button>
          <el-button type="primary" @click="loadData">查询</el-button>
        </el-form-item>
      </el-form>

      <el-alert
        v-if="loadError"
        class="state-block"
        title="积分商品加载失败"
        type="error"
        :closable="false"
        show-icon
      >
        <template slot>
          <span>可以稍后重试，或先检查后端服务状态。</span>
          <el-button type="text" @click="loadData">重新加载</el-button>
        </template>
      </el-alert>

      <template v-else>
        <el-empty v-if="!loading && !list.length" description="没有匹配的积分商品">
          <el-button type="primary" @click="goCreate">去新增积分商品</el-button>
        </el-empty>

        <template v-else>
          <el-table :data="list" border v-loading="loading">
            <el-table-column label="商品" min-width="320">
              <template slot-scope="{ row }">
                <div class="product-cell">
                  <el-image class="product-cell__image" :src="row.mainImage" fit="cover">
                    <div slot="error" class="product-cell__placeholder">无图</div>
                  </el-image>
                  <div class="product-cell__content">
                    <div class="product-cell__title">{{ row.name }}</div>
                    <div class="product-cell__meta">{{ row.subTitle || '暂无副标题' }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="pointsCost" label="兑换积分" width="120" />
            <el-table-column prop="stock" label="库存" width="100" />
            <el-table-column prop="salesCount" label="销量" width="100" />
            <el-table-column label="状态" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="Number(row.status) === 1 ? 'success' : 'info'">
                  {{ Number(row.status) === 1 ? '上架' : '下架' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="240">
              <template slot-scope="{ row }">
                <el-button type="text" @click="goEdit(row.id)">编辑</el-button>
                <el-button type="text" @click="toggleStatus(row)">
                  {{ Number(row.status) === 1 ? '下架' : '上架' }}
                </el-button>
                <el-button type="text" @click="removeRow(row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="table-footer">
            <div class="table-footer__meta">共 {{ total }} 条</div>
            <el-pagination
              background
              layout="prev, pager, next, sizes"
              :current-page="query.pageNum"
              :page-sizes="[10, 20, 50]"
              :page-size="query.pageSize"
              :total="total"
              @current-change="changePage"
              @size-change="changePageSize"
            />
          </div>
        </template>
      </template>
    </el-card>
  </div>
</template>

<script>
import {
  deletePointProduct,
  downPointProduct,
  getPointProductPage,
  upPointProduct
} from '../../api/admin'

const QUERY_KEY = 'mall_admin_point_product_query'

export default {
  data() {
    return {
      query: {
        name: '',
        status: '',
        pageNum: 1,
        pageSize: 20
      },
      list: [],
      total: 0,
      loading: false,
      loadError: false
    }
  },
  created() {
    this.restoreQuery()
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.loadError = false
      try {
        const data = await getPointProductPage(this.query)
        this.list = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.loadError = true
        this.$message.error(error?.message || '积分商品加载失败')
      } finally {
        this.loading = false
      }
      this.persistQuery()
    },
    persistQuery() {
      sessionStorage.setItem(QUERY_KEY, JSON.stringify(this.query))
    },
    restoreQuery() {
      const saved = this.$route.query.q || sessionStorage.getItem(QUERY_KEY)
      if (!saved) {
        return
      }
      try {
        this.query = { ...this.query, ...JSON.parse(saved) }
      } catch (error) {
        sessionStorage.removeItem(QUERY_KEY)
      }
    },
    resetQuery() {
      this.query = {
        name: '',
        status: '',
        pageNum: 1,
        pageSize: 20
      }
      this.loadData()
    },
    changePage(page) {
      this.query.pageNum = page
      this.loadData()
    },
    changePageSize(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    goCreate() {
      this.$router.push({
        path: '/point-mall/products/create',
        query: { q: JSON.stringify(this.query) }
      })
    },
    goEdit(id) {
      this.$router.push({
        path: `/point-mall/products/${id}`,
        query: { q: JSON.stringify(this.query) }
      })
    },
    async toggleStatus(row) {
      const nextStatusText = Number(row.status) === 1 ? '下架' : '上架'
      try {
        if (Number(row.status) === 1) {
          await downPointProduct(row.id)
        } else {
          await upPointProduct(row.id)
        }
        this.$message.success(`已${nextStatusText}`)
        this.loadData()
      } catch (error) {
        this.$message.error(error?.message || `积分商品${nextStatusText}失败`)
      }
    },
    removeRow(row) {
      this.$confirm(`确认删除“${row.name}”吗？`, '删除积分商品', { type: 'warning' })
        .then(async () => {
          try {
            await deletePointProduct(row.id)
            this.$message.success('已删除')
            this.loadData()
          } catch (error) {
            this.$message.error(error?.message || '积分商品删除失败')
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.card-header { display: flex; justify-content: space-between; align-items: center; }
.header-actions { display: flex; gap: 12px; }
.query-form { margin-bottom: 12px; }
.state-block { margin-bottom: 16px; }
.product-cell { display: flex; align-items: center; gap: 12px; }
.product-cell__image, .product-cell__placeholder { width: 56px; height: 56px; border-radius: 10px; }
.product-cell__placeholder { display: flex; align-items: center; justify-content: center; background: #f3f4f6; color: #9ca3af; font-size: 12px; }
.product-cell__title { font-weight: 600; }
.product-cell__meta { margin-top: 4px; color: #6b7280; font-size: 13px; }
.table-footer { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; }
.table-footer__meta { color: #6b7280; font-size: 13px; }
</style>
